/*
 * Which efficiency rate actually describes a platform.
 *
 * "Efficiency" is not one number. A click-oriented platform is read on CTR; a
 * video platform is read on how much of the video was watched, and its CTR is
 * frequently a true, reported, and completely meaningless 0.00% — CTV buys are
 * not sold on clicks, and the ad often is not clickable at all. Printing that
 * 0.00% beside $1.4K of delivery does not state a fact about performance, it
 * invites a conclusion the data never supported.
 *
 * So the metric is chosen per platform, and only from evidence:
 *
 *   - Video-first platforms (CTV, YouTube) are read on VIDEO VIEW RATE — video
 *     views over impressions — whenever both are reported.
 *   - Every other platform is read on CTR.
 *   - A video-first platform with no video evidence gets NO rate rather than a
 *     fallback CTR. Falling back would print exactly the 0.00% this exists to
 *     stop printing.
 *   - A missing, zero or non-numeric denominator yields null everywhere. A rate
 *     is only meaningful over a real, positive, reported basis.
 *
 * The rate VALUE is derived here; the STRING is formatted by the caller's own
 * governed formatter, so a rate reads identically wherever it appears.
 *
 * The same capability evidence also decides AGGREGATE participation. A
 * cross-platform CTR whose denominator includes CTV impressions is not a
 * conservative reading, it is a wrong one: those impressions never had a
 * click to offer, so every one of them dilutes the rate toward a number no
 * platform reported. Each aggregate rate is therefore summed only across the
 * platforms that are eligible for THAT metric — and eligibility comes from
 * the canonical capability sets below plus reported evidence, never from
 * `Number(value) === 0`, because a zero-filled field on an incapable platform
 * is a placeholder, not an observation.
 */

/**
 * Platforms sold and judged on completed viewing rather than clicks.
 *
 * Keyed on the canonical branding key. Membership is about how the inventory
 * is BOUGHT, not about whether a given payload happens to carry video counts —
 * a Meta video campaign still reports and is optimized on clicks.
 */
export const VIDEO_FIRST_PLATFORM_KEYS = Object.freeze(
  new Set(["ctv", "connected_tv", "youtube"])
);

/**
 * Inventory that reports no clicks at all.
 *
 * This is a different question from "is it video-first". Video-first decides which
 * single metric HEADLINES a platform; this decides whether a click rate exists for
 * it in the first place. On this inventory the 0 sitting in the click field is a
 * zero-fill, not an observation, so the platform contributes neither numerator nor
 * denominator to CTR, CPC or conversion rate — on its own or inside a blend.
 *
 * YouTube is deliberately absent. It is video-first, so VVR remains its headline
 * reading, but it reports real clicks against real impressions and its CTR is an
 * honest figure both solo and as part of an aggregate.
 */
export const CLICKLESS_PLATFORM_KEYS = Object.freeze(
  new Set(["ctv", "connected_tv"])
);

/**
 * Platforms with governed video-view REPORTING semantics — the only
 * inventory whose counts may participate in an aggregate video view rate.
 *
 * Broader than the video-first set: TikTok and the Meta family are bought and
 * judged on clicks, but their video views are canonically reported, so when a
 * payload actually carries them the platform belongs under the VVR aggregate.
 * Display-only inventory has no video-view contract at all, so a zero-filled
 * `video_views: 0` on it is a placeholder that must not buy its impressions
 * into anyone's denominator.
 */
export const VIDEO_RATE_CAPABLE_PLATFORM_KEYS = Object.freeze(
  new Set([
    "ctv",
    "connected_tv",
    "youtube",
    "tiktok",
    "meta",
    "facebook",
    "instagram",
  ])
);

/**
 * HOW each platform measures a generic "video view".
 *
 * `VIDEO_RATE_CAPABLE_PLATFORM_KEYS` answers whether a platform reports video
 * metrics at all. It cannot answer the question that actually decides whether
 * two counts may be added: are they counting the same event? They frequently
 * are not, and the field is spelled `videoViews` either way.
 *
 * So the measurement is DECLARED, per platform, and a blend is permitted only
 * within one declared class. Membership is the governed compatibility proof;
 * absence means the platform has no generic video-view contract at all.
 *
 * CTV is deliberately absent, and its absence is the whole point. StackAdapt
 * sends no view column for CTV inventory — what it reports is `vcomp_0`, the
 * START count, an impression whose playback began. That is a real measurement
 * with its own governed KPIs (starts, completed views, completion rate, CPCV);
 * it is simply not a view, and it may never enter a generic Video View Rate
 * or a generic Video Views total. Display is absent for the plainer reason
 * that it has no video contract whatsoever.
 *
 * The Meta family shares one definition across its surfaces, so its keys share
 * a class and legitimately blend with each other. YouTube (a view at thirty
 * seconds or completion) and TikTok (a far shorter threshold) each stand
 * alone: neither may be added to the other, or to Meta's.
 */
export const VIDEO_VIEW_SEMANTIC_CLASSES = Object.freeze({
  youtube: "youtube_trueview",
  meta: "meta_thruplay",
  facebook: "meta_thruplay",
  instagram: "meta_thruplay",
  audience_network: "meta_thruplay",
  messenger: "meta_thruplay",
  tiktok: "tiktok_video_view",
});

/**
 * The declared video-view measurement for a platform, or null when it has none.
 *
 * Null is not a failure to look it up — it is the governed statement that this
 * inventory does not report a compatible generic video view.
 */
export const resolveVideoViewSemanticClass = (platformKey) =>
  VIDEO_VIEW_SEMANTIC_CLASSES[
    String(platformKey == null ? "" : platformKey)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
  ] ?? null;

/**
 * Whether a platform may contribute to a GENERIC video-view basis at all —
 * the numerator of a Video View Rate, the denominator of a CPV, or the Video
 * Views count. Strictly narrower than `VIDEO_RATE_CAPABLE_PLATFORM_KEYS`,
 * which still governs the start- and completion-based metrics CTV does report.
 */
export const platformReportsCompatibleVideoViews = (platformKey) =>
  resolveVideoViewSemanticClass(platformKey) !== null;

/**
 * HOW each platform measures a "video completion".
 *
 * The same question as the view classes above, asked of the other end of the
 * playback. It needs asking for the same reason and it was not being asked:
 * the canonical summary folds `vcomp_95` and `q100`/`views100` into one field
 * spelled `videoCompletions`, so by the time a count reaches this engine the
 * threshold that produced it is gone. The platform is the only surviving
 * provenance, so the threshold is DECLARED against it.
 *
 * StackAdapt's CTV `vcomp_95` fires at 95% of duration. YouTube's "Video
 * played to 100%" fires at 100%. Those are different populations of the same
 * delivery — a 95% count is structurally larger than a 100% count — and
 * neither provider authorises the other's threshold. Sharing the word
 * "completion" is not evidence that two counts may be added: on the live NSC
 * flight adding them produced 185,728, a number that belongs to no
 * measurement and which the campaign's own 141,546 starts cannot support.
 *
 * Each provider keeps its own class rather than being grouped by threshold.
 * A 100% play is not one event across vendors either — autoplay, sound-off
 * and loop handling differ — so compatibility is something a caller proves,
 * not something this table assumes. The Meta surfaces share a class because
 * they share one measurement contract.
 */
export const VIDEO_COMPLETION_SEMANTIC_CLASSES = Object.freeze({
  ctv: "ctv_completed_view_95",
  connected_tv: "ctv_completed_view_95",
  youtube: "youtube_played_to_100",
  tiktok: "tiktok_played_to_100",
  meta: "meta_video_p100",
  facebook: "meta_video_p100",
  instagram: "meta_video_p100",
  audience_network: "meta_video_p100",
  messenger: "meta_video_p100",
});

/**
 * HOW each platform measures a "video start".
 *
 * Declared for the same reason, and deliberately per-provider: a start is an
 * impression whose playback began, and what counts as begun depends on the
 * player. CTV is the only NSC platform that reports one, so this table costs
 * nothing today; what it buys is that a second start-reporting platform
 * cannot silently join the first's total the day it arrives.
 */
export const VIDEO_START_SEMANTIC_CLASSES = Object.freeze({
  ctv: "ctv_video_start",
  connected_tv: "ctv_video_start",
  youtube: "youtube_video_start",
  tiktok: "tiktok_video_start",
  meta: "meta_video_start",
  facebook: "meta_video_start",
  instagram: "meta_video_start",
  audience_network: "meta_video_start",
  messenger: "meta_video_start",
});

/**
 * HOW each platform measures an "engagement".
 *
 * The same declaration the video family already carries, asked of the metric
 * that had none — and the absence was load-bearing. `engagementRate` shipped
 * with `supports: () => true`, so every platform with a populated
 * `engagements` field entered the basis, and the only thing standing between
 * a campaign and a blended nonsense rate was the semantic gate grouping by
 * SOURCE. Source identity is a placeholder, not a ruling: it separates two
 * platforms measuring the same event through different connectors, and it
 * merges two different events the moment they arrive under one source label.
 *
 * What the field actually holds differs per platform, and on a live
 * CTV + Display + YouTube flight it differs in kind, not degree:
 *
 *   YouTube  Google Ads engagements — an interaction with the AD UNIT.
 *   Display  StackAdapt maps `engagements` from `page_starts`, which resolves
 *            from ["page_starts", "landing_page_starts"]. That is a landing
 *            page beginning to load: a post-click SITE event, conceptually
 *            the same family as GA4's engaged sessions — the very metric this
 *            card was just stopped from displaying.
 *   CTV      Nothing. The mapping falls through `page_starts || audio_starts
 *            || clicks` to a click count that non-interactive inventory never
 *            produces, so the 0 is a zero-fill exactly as it is for CTR.
 *
 * Adding tens of thousands of ad interactions to a thousand landing-page
 * loads and dividing by millions of impressions — most of them CTV and
 * Display screens that cannot be engaged — produced 0.85% on the flight that
 * exposed this: a number describing no measurable thing, two orders of
 * magnitude below the rate of the platform that actually earned it. The
 * declaration below is what makes that unreachable.
 *
 * Absence from this table is deliberate and means "no governed engagement
 * signal yet", not "forgotten". A platform joins by a ruling made here, once.
 */
export const ENGAGEMENT_SEMANTIC_CLASSES = Object.freeze({
  youtube: "google_ads_engagement",
  meta: "meta_post_engagement",
  facebook: "meta_post_engagement",
  instagram: "meta_post_engagement",
  audience_network: "meta_post_engagement",
  messenger: "meta_post_engagement",
  tiktok: "tiktok_engagement",
  /* Playback beginning on audio inventory is a real interaction with the ad
     itself, and it is its own event — never added to an ad-unit engagement. */
  audio: "stackadapt_audio_start",
});

/**
 * Inventory whose `engagements` column is populated by something that is not
 * an engagement with the ad.
 *
 * Separated from "reports nothing" because a surface explaining an absent rate
 * needs to tell them apart: Display measures carefully and simply does not
 * measure THIS, while CTV has no engagement contract at all. Collapsing both
 * into "unsupported" would be true of CTV and quietly wrong about Display.
 */
export const SITE_EVENT_ENGAGEMENT_PLATFORM_KEYS = Object.freeze(
  new Set(["display", "mobile", "video", "ingame", "native"])
);

const semanticClassResolver = (table) => (platformKey) =>
  table[
    String(platformKey == null ? "" : platformKey)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
  ] ?? null;

export const resolveVideoCompletionSemanticClass = semanticClassResolver(
  VIDEO_COMPLETION_SEMANTIC_CLASSES
);
export const resolveVideoStartSemanticClass = semanticClassResolver(
  VIDEO_START_SEMANTIC_CLASSES
);
export const resolveEngagementSemanticClass = semanticClassResolver(
  ENGAGEMENT_SEMANTIC_CLASSES
);

export const platformReportsCompatibleVideoCompletions = (platformKey) =>
  resolveVideoCompletionSemanticClass(platformKey) !== null;
export const platformReportsCompatibleVideoStarts = (platformKey) =>
  resolveVideoStartSemanticClass(platformKey) !== null;
export const platformReportsCompatibleEngagements = (platformKey) =>
  resolveEngagementSemanticClass(platformKey) !== null;

export const EFFICIENCY_METRICS = Object.freeze({
  ctr: Object.freeze({
    key: "ctr",
    label: "CTR",
    definition: "Clicks divided by impressions, for the reporting scope.",
  }),
  vvr: Object.freeze({
    key: "vvr",
    label: "VVR",
    definition: "Video views divided by impressions, for the reporting scope.",
  }),
  /*
   * The compact reading for completion-first inventory.
   *
   * Same governed metric as the analytics registry's `vcr` entry ("Completion
   * Rate"); the label differs because the surfaces do. A contribution legend
   * and a platform card have room for three characters beside a percentage,
   * which is why CTR and VVR are spelled that way here too. The explicit
   * "Completion Rate" stays on the KPI strips and dedicated pages, where the
   * metric has a card to itself and a definition beneath it.
   */
  vcr: Object.freeze({
    key: "vcr",
    label: "VCR",
    definition: "Completed views divided by video starts, for the reporting scope.",
  }),
});

/**
 * The governed Completion Rate for one platform's evidence, or null.
 *
 * ONE implementation, because the number appears in several places and they
 * have to agree: the focused CTV KPI strip, the dedicated CTV page, the
 * contribution card and the dashboard contribution legend all resolve it
 * here. Two of those used to derive it inline, which is how a legend and a
 * strip come to state the same campaign's completion rate to different
 * decimals.
 *
 * The precedence is the pre-existing governed one, moved rather than
 * relitigated: the SOURCE's stated rate wins when exactly one channel claimed
 * it, because upstream it is starts-pinned and eligibility-scoped and
 * re-dividing the totals beside it would be a second implementation. More
 * than one contributing channel makes "the last rate stated" meaningless, so
 * the counts answer instead — completions over STARTS, never rebased onto
 * views.
 */
export function resolveCompletionRatePercent({
  completionRate = null,
  completionRateChannels = null,
  videoCompletions = null,
  videoStarts = null,
} = {}) {
  const stated = reportedCount(completionRate);
  if (completionRateChannels === 1 && stated != null) return stated;
  return rateFromCounts(videoCompletions, videoStarts);
}

/**
 * A count as REPORTED, or null. Not a coercion helper: `Number(null)` is 0,
 * and reading an absent count as a reported zero is the exact mistake every
 * function in this module guards against.
 */
const reportedCount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * A percentage from two canonical counts, or null.
 */
export const rateFromCounts = (numerator, denominator) => {
  const value = reportedCount(numerator);
  const basis = reportedCount(denominator);
  if (value == null || basis == null || basis <= 0 || value < 0) return null;
  return (value / basis) * 100;
};

/**
 * The governed efficiency reading for one platform's counts.
 *
 * Returns `{ key, label, definition, value }` or null when the platform's own
 * evidence supports no honest rate. Callers format `value` themselves.
 */
export function resolvePlatformEfficiencyMetric({
  platformKey = null,
  impressions = null,
  clicks = null,
  videoViews = null,
  videoStarts = null,
  videoCompletions = null,
  completionRate = null,
  completionRateChannels = null,
} = {}) {
  const key = String(platformKey || "").trim().toLowerCase();
  const videoFirst = VIDEO_FIRST_PLATFORM_KEYS.has(key);

  if (videoFirst) {
    /*
     * Video inventory is read on the video metric it can actually report, in
     * declared order — not on one rate forced across incompatible media.
     *
     * A generic VIDEO VIEW RATE, but only where the platform declares a
     * compatible generic view (YouTube does; see
     * `VIDEO_VIEW_SEMANTIC_CLASSES`). Otherwise the COMPLETION RATE, which is
     * what completion-first inventory measures.
     *
     * CTV takes the second branch by construction, and that is the point.
     * It declares no generic view, so it never reads on VVR — the 99.4% it
     * used to print was its START count over its impressions, wearing a view
     * rate's label. Correcting that left CTV with no statistic at all, which
     * was honest and useless: the buy is judged on how much of the video
     * finished, and it reports exactly that. Now it says so.
     */
    if (platformReportsCompatibleVideoViews(key)) {
      const viewRate = rateFromCounts(videoViews, impressions);
      if (viewRate != null) return { ...EFFICIENCY_METRICS.vvr, value: viewRate };
    }
    const completion = resolveCompletionRatePercent({
      completionRate,
      completionRateChannels,
      videoCompletions,
      videoStarts,
    });
    if (completion != null) return { ...EFFICIENCY_METRICS.vcr, value: completion };
    // No video evidence ⇒ no reading. Never the CTR fallback: on this
    // inventory that is the meaningless 0.00% we refuse to print.
    return null;
  }

  const value = rateFromCounts(clicks, impressions);
  return value == null ? null : { ...EFFICIENCY_METRICS.ctr, value };
}

/**
 * The canonical counts a platform row carries, whatever shape it arrived in.
 *
 * Canonical `byChannel` entries are snake_cased at the entry level
 * (`video_views`) while the platform inventory and the report model are
 * camelCased (`videoViews`). Both are the same observation; neither is
 * preferred over the other, and an absent count stays absent.
 */
export const readPlatformCounts = (row = {}) => ({
  impressions: row?.impressions ?? null,
  clicks: row?.clicks ?? null,
  videoViews:
    row?.trueViewViews ?? row?.true_view_views ?? row?.videoViews ?? row?.video_views ?? null,
  /* Starts and completions travel with the views, because the platforms that
     report no view report these — and a resolver asked which metric describes
     a platform cannot answer without the evidence it would answer FROM. Read
     under their own names, never off `videoViews`. */
  videoStarts:
    row?.videoStarts ?? row?.video_started ?? row?.videoStarted ?? row?.video_starts ?? null,
  videoCompletions:
    row?.videoCompletions ??
    row?.video_completions ??
    row?.video_completed ??
    row?.videoCompleted ??
    null,
  /* The source's own stated completion rate, and how many channels claimed
     one — the two inputs the governed precedence needs. */
  completionRate:
    row?.completionRate ?? row?.videoCompletionRate ?? row?.video_completion_rate ?? null,
  completionRateChannels: row?.completionRateChannels ?? null,
});

/**
 * Whether ONE platform's counts may participate in each aggregate rate.
 *
 * Participation is capability AND evidence, per metric:
 *
 *   - CTR: the platform must be click inventory (not video-first — CTV and
 *     YouTube impressions never had a click to offer, so they may not sit
 *     under anyone's click rate), with reported impressions to divide by and
 *     a reported click count, zero included. `clicks: 0` on CTV does not buy
 *     its way in: the capability gate, not the zero, decides.
 *   - VVR: the platform must carry governed video-view reporting semantics,
 *     with reported impressions and a reported video-view count. Display's
 *     zero-filled `video_views: 0` is a placeholder on inventory with no
 *     video contract, so it stays out of both the numerator AND the
 *     denominator.
 *
 * The distinction this preserves: eligible + observed 0 is a real zero and
 * participates; metric-not-applicable / not-reported is excluded entirely.
 *
 * Both memberships can be true at once — TikTok or Meta video inventory
 * reports clicks and video views, and participates in both aggregates. That
 * is different from `resolvePlatformEfficiencyMetric` above, which answers
 * "which ONE rate is this platform read on" for a card or tooltip; this
 * answers "which aggregate denominators may these impressions enter".
 */
export function resolvePlatformRateParticipation({
  platformKey = null,
  impressions = null,
  clicks = null,
  videoViews = null,
} = {}) {
  const key = String(platformKey || "").trim().toLowerCase();
  const reportedImpressions = reportedCount(impressions);
  const reportedClicks = reportedCount(clicks);
  const reportedVideoViews = reportedCount(videoViews);
  const divisible = reportedImpressions != null && reportedImpressions > 0;
  return {
    ctr: {
      eligible:
        !CLICKLESS_PLATFORM_KEYS.has(key) &&
        divisible &&
        reportedClicks != null &&
        reportedClicks >= 0,
      clicks: reportedClicks,
      impressions: reportedImpressions,
    },
    vvr: {
      eligible:
        platformReportsCompatibleVideoViews(key) &&
        divisible &&
        reportedVideoViews != null &&
        reportedVideoViews >= 0,
      videoViews: reportedVideoViews,
      impressions: reportedImpressions,
      /* The measurement these counts came from — the compatibility proof an
         aggregate needs before it may add them to anyone else's. */
      semanticClass: resolveVideoViewSemanticClass(key),
    },
  };
}

/**
 * The aggregate rate basis for a set of platform rows: each governed rate,
 * summed ONLY across the platforms eligible for that rate —
 * SUM(numerator over eligible platforms) / SUM(impressions over those SAME
 * platforms) — never one all-platform impression pool under every rate.
 *
 * Rows are `{ platformKey, sourceKey, impressions, clicks, videoViews }`
 * (spread `readPlatformCounts` for canonical casing). Each rate's `value` is
 * null when no platform was eligible, and `platformKeys` names exactly the
 * platforms whose counts the rate divides, so a surface or a test can state
 * the scope instead of implying "all of them".
 *
 * VVR carries one further rule, which CTR does not need: a click is a click
 * wherever it happens, but a "video view" is defined by whoever measured it.
 * A CTV view is an impression whose playback began; YouTube counts one at
 * thirty seconds or completion. Summing those and dividing by their combined
 * impressions yields a percentage that measures nothing, so a VVR spanning
 * more than one measurement is declined rather than manufactured, and each
 * platform states its own rate instead.
 *
 * Compatibility is read from the DECLARED measurement
 * (`VIDEO_VIEW_SEMANTIC_CLASSES`), not from which data source delivered the
 * row. Source identity was the wrong proxy in both directions: it declined
 * YouTube and TikTok arriving from different connectors even though neither
 * could ever have blended anyway, and — worse — it would have blended CTV
 * starts with YouTube views the moment both happened to arrive under one
 * source label. The declared class asks the only question that matters.
 *
 * Ineligibility EXCLUDES; it does not invalidate. A platform with no
 * compatible measurement contributes nothing to either side of the rate, and
 * the platforms that do measure it still state theirs — including when only
 * one of them does. The two engines answer identically by construction;
 * `metricBasis.js` shares this module's declarations to keep it that way.
 */
export function aggregateEligibleRateBasis(rows = []) {
  const basis = {
    ctr: { value: null, clicks: 0, impressions: 0, platformKeys: [] },
    vvr: {
      value: null,
      videoViews: 0,
      impressions: 0,
      platformKeys: [],
      comparable: true,
      semanticClass: null,
    },
  };
  /* Eligible video-view observations, kept in their measurement groups so the
     numerator and the denominator can never come from different ones. */
  const vvrGroups = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const participation = resolvePlatformRateParticipation(row);
    if (participation.ctr.eligible) {
      basis.ctr.clicks += participation.ctr.clicks;
      basis.ctr.impressions += participation.ctr.impressions;
      basis.ctr.platformKeys.push(row?.platformKey ?? null);
    }
    if (participation.vvr.eligible) {
      const semanticClass = participation.vvr.semanticClass;
      const group = vvrGroups.get(semanticClass) || {
        videoViews: 0,
        impressions: 0,
        platformKeys: [],
      };
      group.videoViews += participation.vvr.videoViews;
      group.impressions += participation.vvr.impressions;
      group.platformKeys.push(row?.platformKey ?? null);
      vvrGroups.set(semanticClass, group);
    }
  });
  basis.ctr.value = basis.ctr.platformKeys.length
    ? rateFromCounts(basis.ctr.clicks, basis.ctr.impressions)
    : null;

  const groups = [...vvrGroups.entries()];

  /*
   * More than one MEASUREMENT in the numerator means the sum is not a
   * measurement — and there is no governed cross-definition view, so the rate
   * is unavailable rather than arbitrarily taken from the largest group. Keep
   * the eligible counts visible for diagnostics.
   *
   * One group is the ordinary case, and one group of one platform is still a
   * valid aggregate: a campaign may run five platforms while only one of them
   * measures a compatible view. The others were never observations of this
   * metric, so they are excluded from BOTH sides of it — they do not zero-fill
   * the numerator, they do not sell their impressions into the denominator,
   * and they do not make an otherwise valid rate disappear.
   */
  if (groups.length > 1) {
    basis.vvr.comparable = false;
    basis.vvr.videoViews = groups.reduce((sum, [, g]) => sum + g.videoViews, 0);
    basis.vvr.impressions = groups.reduce((sum, [, g]) => sum + g.impressions, 0);
    basis.vvr.platformKeys = [];
    basis.vvr.value = null;
    return basis;
  }

  if (groups.length === 1) {
    const [semanticClass, group] = groups[0];
    basis.vvr.videoViews = group.videoViews;
    basis.vvr.impressions = group.impressions;
    basis.vvr.platformKeys = group.platformKeys;
    basis.vvr.semanticClass = semanticClass;
    basis.vvr.value = rateFromCounts(group.videoViews, group.impressions);
  }
  return basis;
}
